import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { fetchWithCacheFallback } from '../services/api-client.service';
import { useMobileShell } from '../hooks/useMobileShell';

interface RentHistoryMonth {
  monthYear: string;
  amountPaid: number;
  paymentDate?: string;
  paymentMethod?: 'Online' | 'Cash';
  rentDueDate: string;
  status: 'paid' | 'unpaid' | 'partial';
}

interface RentHistoryResident {
  id: string;
  bedNumber: string;
  name: string;
  monthStartDate: string;
  roomRent: number;
  months: RentHistoryMonth[];
}

const Reports: React.FC = () => {
  const isMobile = useMobileShell();
  const [rentHistory, setRentHistory] = useState<RentHistoryResident[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startMonth, setStartMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 5);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [endMonth, setEndMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const loadRentHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startMonth) params.set('startMonth', startMonth);
      if (endMonth) params.set('endMonth', endMonth);
      const { data } = await fetchWithCacheFallback<{ success: boolean; data: { residents: RentHistoryResident[] } }>(
        `/api/reports/rent-history?${params}`
      );
      setRentHistory(data?.data?.residents ?? []);
    } catch (err) {
      setError('Failed to load rent history');
      setRentHistory([]);
    } finally {
      setLoading(false);
    }
  }, [startMonth, endMonth]);

  useEffect(() => {
    loadRentHistory();
  }, [loadRentHistory]);

  const formatMonth = (m: string) => {
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  };

  const getStatusColor = (s: string) => (s === 'paid' ? 'success' : s === 'partial' ? 'warning' : 'default');

  const months = rentHistory[0]?.months?.map((m) => m.monthYear) ?? [];

  const RentHistoryCards = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {rentHistory.map((r) => (
        <Card key={r.id} variant="outlined">
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>{r.name}</Typography>
                <Chip label={r.bedNumber} color="primary" variant="outlined" size="small" sx={{ mt: 0.5 }} />
              </Box>
              <Typography variant="body2" fontWeight={600}>₹{r.roomRent.toLocaleString()}/mo</Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              {r.months.map((mo) => (
                <Box key={mo.monthYear} display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">{formatMonth(mo.monthYear)}</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">₹{mo.amountPaid.toLocaleString()}</Typography>
                    <Chip label={mo.status} size="small" color={getStatusColor(mo.status)} />
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const RentHistoryTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Bed</strong></TableCell>
            <TableCell><strong>Name</strong></TableCell>
            <TableCell><strong>Room Rent</strong></TableCell>
            {months.map((m) => (
              <TableCell key={m} align="center">
                <strong>{formatMonth(m)}</strong>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rentHistory.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.bedNumber}</TableCell>
              <TableCell>{r.name}</TableCell>
              <TableCell>₹{r.roomRent.toLocaleString()}</TableCell>
              {r.months.map((mo) => (
                <TableCell key={mo.monthYear} align="center">
                  <Box>
                    <Typography variant="body2">₹{mo.amountPaid.toLocaleString()}</Typography>
                    <Chip label={mo.status} size="small" color={getStatusColor(mo.status)} sx={{ mt: 0.5 }} />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Reports & Analytics
      </Typography>
      <Typography variant="body1" color="textSecondary" mb={3}>
        Generate comprehensive reports and financial analytics.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Rent History Report (FR-046)
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Full rent history of all residents month-wise.
          </Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="Start Month"
                type="month"
                value={startMonth}
                onChange={(e) => setStartMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="End Month"
                type="month"
                value={endMonth}
                onChange={(e) => setEndMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4} md="auto">
              <Button
                variant="contained"
                onClick={loadRentHistory}
                disabled={loading}
                fullWidth
                sx={{ minHeight: 44 }}
              >
                {loading ? 'Loading...' : 'Load Report'}
              </Button>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : rentHistory.length === 0 ? (
            <Alert severity="info">No residents or no data for the selected period.</Alert>
          ) : isMobile ? (
            <RentHistoryCards />
          ) : (
            <RentHistoryTable />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Other Reports
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Additional report types (P&amp;L, occupancy, collection efficiency) available via API.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Reports;